/**
 * Date and time formatting utilities for Leen Frontend.
 * Ensures consistent presentation of dates and times matching backend local timestamps
 * without browser timezone offset shifts.
 */

export function parseToUtcDate(val: any): Date | null {
  if (val === null || val === undefined || val === '') return null;
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    return val;
  }
  const str = String(val).trim();
  if (!str) return null;

  // Extract YYYY-MM-DD and optional HH:mm:ss[.sss]
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?)?/);
  if (match) {
    const [, y, m, d, hh, mm, ss] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10) - 1;
    const day = parseInt(d, 10);
    const hours = hh !== undefined ? parseInt(hh, 10) : 0;
    const minutes = mm !== undefined ? parseInt(mm, 10) : 0;
    const seconds = ss !== undefined ? parseInt(ss, 10) : 0;
    return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
  }

  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Format a timestamp for display as '02 Nov 2026, 17:48' in British English format without browser timezone shift.
 */
export function formatDisplayDateTime(val: any, fallbackStr: string = '—'): string {
  const d = parseToUtcDate(val);
  if (!d) return typeof val === 'string' && val.trim() ? val : fallbackStr;
  return d.toLocaleString('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format a date for display as 'DD/MM/YYYY' (e.g. '31/07/2025') without browser timezone shift.
 */
export function formatDisplayDate(val: any, fallbackStr: string = '—'): string {
  const d = parseToUtcDate(val);
  if (!d) return typeof val === 'string' && val.trim() ? val : fallbackStr;
  return d.toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format timestamp into { date: 'DD/MM/YY', time: 'HH:mm' } for Action Logs and tables.
 */
export function formatTimestampParts(val: any): { date: string; time: string } {
  const d = parseToUtcDate(val);
  if (!d) return { date: '—', time: '—' };
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = String(d.getUTCFullYear()).slice(-2);
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return {
    date: `${day}/${month}/${year}`,
    time: `${hours}:${minutes}`,
  };
}
